import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Text,
} from 'recharts';
import { Copy, Check, Download, Loader2 } from 'lucide-react';
import { useCallback, useRef } from 'react';
import useTranslation from '@/hooks/useTranslation';
import categoriesColorList from '@/utils/categoriesColorList';
import useCopyState from './useCopyState';
import useDownloadImage from './useDownloadImage';

interface ArtifactChartBlockProps {
  title: string;
  data: {
    x_label?: string;
    y_label?: string;
    series: Array<{ name: string; value: number; color?: string }>;
  };
}

const DEFAULT_COLORS = categoriesColorList;

/**
 * Characters that roughly fit in one pixel at 11 px font-size in Inter.
 * Recharts Text uses canvas measurement internally, so this is only used for
 * the YAxis `width` computation (not for actual wrapping).  Inter characters
 * average ~6.2 px each at 11 px size.
 */
const CHAR_PX_ESTIMATE = 6.2;
const Y_AXIS_MIN = 90;
const Y_AXIS_MAX = 200;

/**
 * Compute an appropriate YAxis width from the longest label string.
 * We cap between MIN and MAX so the chart doesn't look odd on either extreme.
 */
function computeYAxisWidth(series: Array<{ name: string }>): number {
  const longest = Math.max(...series.map((s) => s.name.length));
  const raw = Math.ceil(longest * CHAR_PX_ESTIMATE);
  return Math.min(Math.max(raw, Y_AXIS_MIN), Y_AXIS_MAX);
}

/**
 * Estimate how many lines a label will wrap to given a container width.
 * Used to scale the per-row height so wrapped rows don't overlap.
 */
function estimateLineCount(label: string, widthPx: number): number {
  const charsPerLine = Math.max(1, Math.floor(widthPx / CHAR_PX_ESTIMATE));
  return Math.ceil(label.length / charsPerLine);
}

/** Base px per data row when labels are a single line. */
const BASE_ROW_HEIGHT = 48;
/** Minimum chart height in px. */
const CHART_MIN_HEIGHT = 200;

interface CustomYAxisTickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
  /** Width available for the tick label — passed from YAxis. */
  width?: number;
}

/**
 * Custom YAxis tick that wraps long labels instead of truncating them.
 * Uses Recharts' own `<Text>` component which handles multi-line SVG text
 * natively via `width` + `verticalAnchor`.
 */
function WrappedYAxisTick({ x = 0, y = 0, payload, width = 120 }: CustomYAxisTickProps) {
  if (!payload?.value) return null;
  return (
    <Text
      x={x}
      y={y}
      width={width}
      textAnchor="end"
      verticalAnchor="middle"
      fontSize={11}
      fill="#525252"
      lineHeight="1.3em"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      {payload.value}
    </Text>
  );
}

function ArtifactChartBlock({ title, data }: ArtifactChartBlockProps) {
  const { lang } = useTranslation();
  const [copied, triggerCopied] = useCopyState();
  const chartCardRef = useRef<HTMLDivElement>(null);
  const [isDownloading, isDone, triggerDownload] = useDownloadImage(chartCardRef, { title });

  const handleCopy = useCallback(() => {
    const text = data.series.map((s) => `${s.name}\t${s.value}`).join('\n');
    navigator.clipboard
      .writeText(`${title}\n${data.x_label || ''}\t${data.y_label || ''}\n${text}`)
      .then(() => triggerCopied())
      .catch(() => {});
  }, [data, title, triggerCopied]);

  if (!data?.series?.length) return null;

  const yAxisWidth = computeYAxisWidth(data.series);

  // Compute chart height: for each row we estimate how many lines the label
  // will wrap to, then use that to scale the row height. A single-line label
  // uses BASE_ROW_HEIGHT; each additional line adds half a row-height.
  const chartHeight = Math.max(
    CHART_MIN_HEIGHT,
    data.series.reduce((total, s) => {
      const lines = estimateLineCount(s.name, yAxisWidth);
      return total + BASE_ROW_HEIGHT + Math.max(0, lines - 1) * (BASE_ROW_HEIGHT * 0.5);
    }, 0),
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-800">{title}</h3>
        <div className="flex items-center gap-0.5">
          {/* Download PNG */}
          <button
            type="button"
            onClick={triggerDownload}
            disabled={isDownloading}
            className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            title={lang.get('msg.downloadChartPng')}
            aria-label={lang.get('msg.downloadChartPng')}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {isDownloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : isDone ? (
              <Check className="h-3.5 w-3.5 text-primary" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
          </button>

          {/* Copy data */}
          <button
            type="button"
            onClick={handleCopy}
            className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            title={lang.get('msg.copyArtifact')}
            aria-label={lang.get('msg.copyArtifact')}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-primary" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      </div>

      {/* White card — this is what gets captured by html-to-image */}
      <div ref={chartCardRef} className="bg-white border border-neutral-200 rounded-lg p-4">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={data.series}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: '#737373' }}
              label={
                data.y_label
                  ? { value: data.y_label, position: 'insideBottom', offset: -5, fontSize: 11 }
                  : undefined
              }
            />
            <YAxis
              type="category"
              dataKey="name"
              width={yAxisWidth}
              tick={<WrappedYAxisTick width={yAxisWidth} />}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: '1px solid #e5e5e5',
                fontSize: 12,
              }}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {data.series.map((entry, idx) => (
                <Cell
                  key={entry.name}
                  fill={entry.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default ArtifactChartBlock;
